import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Sparkles, Users, Shield, TrendingUp, Check, ArrowRight, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JoinPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/invite/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitted(true);
        toast.success('Check your email for the invite link!');
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: ShoppingBag, title: 'Buy & Sell', desc: 'Pre-loved fashion at great prices' },
    { icon: Users, title: 'Local Community', desc: 'Connect with sellers near you' },
    { icon: Shield, title: 'Safe & Secure', desc: 'Verified users & secure payments' },
    { icon: TrendingUp, title: 'Earn Money', desc: 'Turn your wardrobe into cash' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-minted-600 via-minted-500 to-emerald-400 flex items-center justify-center px-4">
        <Toaster position="top-center" richColors />
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-20 h-20 bg-minted-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-minted-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Check Your Email! 📬</h2>
          <p className="text-gray-600 mb-6">
            We've sent a magic link to <strong>{formData.email}</strong>. 
            Click it to complete your signup and start exploring!
          </p>
          <div className="bg-minted-50 rounded-xl p-4 text-left">
            <p className="text-sm text-minted-700 font-medium mb-2">What's next?</p>
            <ul className="text-sm text-minted-600 space-y-1">
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Open the email from Minted</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Click "Complete Your Signup"</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Set your password & start shopping!</li>
            </ul>
          </div>
          <p className="text-xs text-gray-400 mt-6">
            Didn't receive it? Check your spam folder or <button onClick={() => setSubmitted(false)} className="text-minted-600 underline">try again</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-minted-600 via-minted-500 to-emerald-400">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="px-6 py-8">
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-lg">
            <ShoppingBag className="w-7 h-7 text-minted-600" />
          </div>
          <h1 className="text-3xl font-bold text-white">Minted</h1>
        </div>
        <p className="text-minted-100 text-center">Buy & Sell Near You</p>
      </div>

      {/* Hero Section */}
      <div className="px-6 text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
          <Sparkles className="w-4 h-4 text-yellow-300" />
          <span className="text-white text-sm font-medium">Join 100+ sellers already listing</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
          Turn Your Wardrobe<br />Into Cash 💰
        </h2>
        <p className="text-minted-100 text-lg max-w-md mx-auto">
          The marketplace for pre-loved fashion. Buy unique pieces, sell what you don't wear.
        </p>
      </div>

      {/* Signup Card */}
      <div className="px-6 pb-8">
        <div className="bg-white rounded-3xl p-6 shadow-2xl max-w-md mx-auto">
          <h3 className="text-xl font-bold text-gray-900 text-center mb-1">Get Started Free</h3>
          <p className="text-gray-500 text-center text-sm mb-6">No credit card required</p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="h-12 text-base"
                data-testid="join-name-input"
              />
            </div>
            <div>
              <Input
                type="email"
                placeholder="Your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-12 text-base"
                data-testid="join-email-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base bg-minted-600 hover:bg-minted-700 gap-2"
              disabled={loading}
              data-testid="join-submit-btn"
            >
              {loading ? 'Sending...' : (
                <>
                  Join Minted <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-4">
            By joining, you agree to our{' '}
            <Link to="/terms" className="text-minted-600 underline">Terms</Link> &{' '}
            <Link to="/privacy" className="text-minted-600 underline">Privacy Policy</Link>
          </p>

          <div className="border-t mt-6 pt-6">
            <p className="text-center text-sm text-gray-500 mb-4">Already have an account?</p>
            <Link to="/login">
              <Button variant="outline" className="w-full h-11">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="px-6 pb-12">
        <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
          {features.map((feature, i) => (
            <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center">
              <feature.icon className="w-8 h-8 text-white mx-auto mb-2" />
              <h4 className="text-white font-semibold text-sm">{feature.title}</h4>
              <p className="text-minted-100 text-xs mt-1">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Social Proof */}
      <div className="px-6 pb-12 text-center">
        <p className="text-white/80 text-sm">
          🛍️ <strong className="text-white">18+ items</strong> listed this week
        </p>
      </div>
    </div>
  );
};

export default JoinPage;
